if (!customElements.get('cross-sell-carousel')) {
  class CrossSellCarousel extends HTMLElement {
    constructor() {
      super()
      this.carousel = this.querySelector('[data-cross-sell-carousel]')
      this.items = this.querySelectorAll('[data-cross-sell-item]')
      this.prevButton = this.querySelector('[data-cross-sell-prev]')
      this.nextButton = this.querySelector('[data-cross-sell-next]')
      this.cards = this.querySelectorAll('[data-cross-sell-card]')

      this.quickAdd = this.querySelector('[data-cross-sell-quick-add]')
      this.overlay = this.querySelector('[data-cross-sell-overlay]')
      this.closeButton = this.querySelector('[data-cross-sell-quick-add-close]')
      this.sizesContainer = this.querySelector('[data-cross-sell-sizes]')
      this.submitButton = this.querySelector('[data-cross-sell-submit]')
      this.colourValue = this.querySelector('[data-colour-value]')

      this.currentProductHandle = null
      this._boundKeyup = null
      this._resizeObserver = null
    }

    connectedCallback() {
      if (!this.carousel || this.items.length === 0) return

      this.initCarousel()
      this.initCards()
      this.initQuickAdd()
      this.moveQuickAddToDrawer()
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
      }
      if (this._boundKeyup) {
        document.removeEventListener('keyup', this._boundKeyup)
        this._boundKeyup = null
      }
      // Clean up reparented nodes so they don't orphan in the cart-drawer
      if (this._movedOverlay) {
        this._movedOverlay.remove()
        this._movedOverlay = null
      }
      if (this._movedQuickAdd) {
        this._movedQuickAdd.remove()
        this._movedQuickAdd = null
      }
    }

    moveQuickAddToDrawer() {
      // Reparent into cart-drawer (the custom element), not .drawer__inner,
      // because .drawer__inner content is replaced on cart re-renders
      const cartDrawer = this.closest('cart-drawer')
      if (cartDrawer && this.overlay && this.quickAdd) {
        cartDrawer.appendChild(this.overlay)
        cartDrawer.appendChild(this.quickAdd)
        this._movedOverlay = this.overlay
        this._movedQuickAdd = this.quickAdd
      }
    }

    initCarousel() {
      this.carousel.addEventListener('scroll', this.updateNavButtons.bind(this))
      this.prevButton?.addEventListener('click', this.scrollPrev.bind(this))
      this.nextButton?.addEventListener('click', this.scrollNext.bind(this))

      this.updateNavButtons()

      this._resizeObserver = new ResizeObserver(() => this.updateNavButtons())
      this._resizeObserver.observe(this.carousel)
    }

    updateNavButtons() {
      if (!this.carousel) return

      const scrollLeft = this.carousel.scrollLeft
      const scrollWidth = this.carousel.scrollWidth
      const clientWidth = this.carousel.clientWidth
      const maxScroll = scrollWidth - clientWidth

      if (this.prevButton) {
        this.prevButton.disabled = scrollLeft <= 1
      }
      if (this.nextButton) {
        this.nextButton.disabled = scrollLeft >= maxScroll - 1
      }
    }

    scrollPrev() {
      if (!this.items.length) return
      const itemWidth = this.items[0].offsetWidth
      const gap = parseInt(getComputedStyle(this.carousel).gap) || 16
      this.carousel.scrollBy({ left: -(itemWidth + gap), behavior: 'smooth' })
    }

    scrollNext() {
      if (!this.items.length) return
      const itemWidth = this.items[0].offsetWidth
      const gap = parseInt(getComputedStyle(this.carousel).gap) || 16
      this.carousel.scrollBy({ left: itemWidth + gap, behavior: 'smooth' })
    }

    initCards() {
      this.cards.forEach(card => {
        const swatches = card.querySelectorAll(
          '[data-cross-sell-swatches] button'
        )
        const addButton = card.querySelector('[data-cross-sell-add-button]')

        swatches.forEach(swatch => {
          swatch.addEventListener('click', e => this.handleSwatchClick(e, card))
        })

        addButton?.addEventListener('click', e => this.openQuickAdd(e, card))
      })
    }

    initQuickAdd() {
      this.closeButton?.addEventListener('click', () => this.closeQuickAdd())
      this.overlay?.addEventListener('click', () => this.closeQuickAdd())
      this.submitButton?.addEventListener('click', () => this.handleSubmit())

      this._boundKeyup = e => {
        if (
          e.key === 'Escape' &&
          this.quickAdd?.classList.contains('is-open')
        ) {
          this.closeQuickAdd()
        }
      }
      document.addEventListener('keyup', this._boundKeyup)
    }

    handleSwatchClick(event, card) {
      event.preventDefault()
      event.stopPropagation()

      const swatch = event.currentTarget
      const swatches = card.querySelectorAll(
        '[data-cross-sell-swatches] button'
      )
      const image = card.querySelector('[data-cross-sell-image]')
      const title = card.querySelector('[data-cross-sell-title]')
      const addButton = card.querySelector('[data-cross-sell-add-button]')

      swatches.forEach(s => s.classList.remove('is-active'))
      swatch.classList.add('is-active')

      const productHandle = swatch.dataset.productHandle
      const productTitle = swatch.dataset.productTitle
      const featuredImage = swatch.dataset.featuredImage
      const colourName = swatch.dataset.colourName

      card.dataset.productHandle = productHandle

      if (addButton) {
        addButton.dataset.productHandle = productHandle
        addButton.dataset.colourName = colourName
      }

      if (image && featuredImage) {
        image.src = featuredImage
        image.srcset = `${featuredImage} 1x, ${featuredImage.replace('width=172', 'width=344')} 2x`
      }

      if (title && productTitle) {
        title.textContent = productTitle
      }
    }

    async openQuickAdd(event, card) {
      event.preventDefault()
      event.stopPropagation()

      const addButton = event.currentTarget
      const productHandle =
        addButton.dataset.productHandle || card.dataset.productHandle
      const colourName = addButton.dataset.colourName || ''

      if (!productHandle) return

      this.currentProductHandle = productHandle

      if (this.colourValue) {
        this.colourValue.textContent = colourName
      }

      await this.loadProductSizes(productHandle)

      this.overlay?.classList.add('is-open')
      this.quickAdd?.classList.add('is-open')
    }

    async loadProductSizes(productHandle) {
      if (!this.sizesContainer) return

      this.sizesContainer.innerHTML =
        `<span class="cross-sell-quick-add__loading">${window.cartStrings?.loading || 'Loading...'}</span>`
      if (this.submitButton) this.submitButton.disabled = true

      try {
        const response = await fetch(`/products/${productHandle}.js`)
        if (this.currentProductHandle !== productHandle) return
        if (!response.ok) {
          throw new Error(`Product fetch failed: ${response.status}`)
        }
        const product = await response.json()

        const sizeOptionIndex = product.options.findIndex(
          opt => (typeof opt === 'string' ? opt : opt.name || '').toLowerCase() === 'size'
        )

        this.sizesContainer.innerHTML = ''

        product.variants.forEach(variant => {
          const sizeValue =
            sizeOptionIndex >= 0
              ? variant.options[sizeOptionIndex]
              : variant.title

          const button = document.createElement('button')
          button.type = 'button'
          button.className = 'cross-sell-quick-add__size'
          button.dataset.variantId = variant.id
          button.dataset.sizeValue = sizeValue
          button.textContent = sizeValue
          button.disabled = !variant.available
          button.setAttribute('aria-label', sizeValue)

          button.addEventListener('click', e => this.handleSizeSelect(e))
          this.sizesContainer.appendChild(button)
        })
      } catch (error) {
        console.error('Error fetching product data:', error)
        this.sizesContainer.innerHTML =
          `<span class="cross-sell-quick-add__error">${window.cartStrings?.errorLoadingSizes || 'Error loading sizes'}</span>`
      }
    }

    closeQuickAdd() {
      this.overlay?.classList.remove('is-open')
      this.quickAdd?.classList.remove('is-open')

      const sizeButtons = this.sizesContainer?.querySelectorAll('button')
      sizeButtons?.forEach(btn => btn.classList.remove('is-active'))

      if (this.submitButton) {
        this.submitButton.disabled = true
        delete this.submitButton.dataset.selectedVariantId
      }

      this.currentProductHandle = null
    }

    handleSizeSelect(event) {
      event.preventDefault()
      event.stopPropagation()

      const button = event.currentTarget
      if (button.disabled) return

      const sizeButtons = this.sizesContainer?.querySelectorAll('button')
      sizeButtons?.forEach(btn => btn.classList.remove('is-active'))
      button.classList.add('is-active')

      if (this.submitButton) {
        this.submitButton.disabled = false
        this.submitButton.dataset.selectedVariantId = button.dataset.variantId
      }
    }

    async handleSubmit() {
      if (!this.submitButton || this.submitButton.disabled) return

      const variantId = this.submitButton.dataset.selectedVariantId
      if (!variantId) return

      const spinner = this.submitButton.querySelector('.loading__spinner')
      this.submitButton.disabled = true
      spinner?.classList.remove('hidden')

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        })

        if (!response.ok) {
          throw new Error(`Cart add failed: ${response.status}`)
        }

        const result = await response.json()

        this.closeQuickAdd()

        const cartDrawer = document.querySelector('cart-drawer')
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(result)
        }
      } catch (error) {
        console.error('Error adding to cart:', error)
        this.submitButton.disabled = false
      } finally {
        spinner?.classList.add('hidden')
      }
    }
  }

  customElements.define('cross-sell-carousel', CrossSellCarousel)
}
